import {
  toWSDate,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPage,
  WSPill,
  WSSectionToolbar,
  WSTabs,
  WSText
} from "@wingspanhq/fe-component-library";
import { EngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { useQueryEngagement } from "../../../../query/engagements/queries/useQueryEngagement";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryWorkDefinitions";
import { WSQueries } from "../../../../query/WSQuery";
import { PageHeader } from "../../../../shared/components/PageHeader";
import { useAddRequirementToEngagementModal } from "../../../Payees/components/AddRequirementToEngagementModal";
import { useCreatePayeeEngagementModal } from "../../../Payees/components/CreatePayeeEngagementModal/useCreateEngagementModal";
import { EngagementInfoPanel } from "../../../Payers/components/EngagementInfoPanel";
import { RateCardViewWidget } from "../../../RateCards";
import { TableWorkDefinitions } from "../../../WorkDefinitions";
import { useEngagementTypeDefaultActions } from "../../hooks/useEngagementTypeDefaultActions";
import { useEditEngagementModal } from "../../modals/EditEngagementModal";
import {
  getSectionPathByEngagementStatus,
  useGoToEngagementTypes
} from "../../paths";
import { RecentContractorsWidget } from "./RecentContractorsWidget";
import { RequirementsTable } from "./RequirementsTable";
import { EngagementTypeHistoryWidget } from "./useEngagementTypeHistoryWidget";

export const RouteEngagementTypeDetails: React.FC<
  RouteComponentProps<{
    engagementTypeId: string;
  }>
> = ({
  match: {
    params: { engagementTypeId }
  },
  history,
  location
}) => {
  const goToEngagementTypes = useGoToEngagementTypes();
  const queryEngagement = useQueryEngagement(engagementTypeId);

  // Extract work definition IDs from engagement (once loaded)
  const workDefinitionIds =
    queryEngagement.data?.workDefinitions?.map(wd => wd.workDefinitionId) || [];

  // Fetch work definitions filtered by the engagement's attached IDs
  const queryWorkDefinitions = useQueryWorkDefinitions(
    {
      "filter[workDefinitionId][in]": workDefinitionIds,
      sort: { name: "asc" }
    },
    { enabled: workDefinitionIds.length > 0 }
  );

  const editEngagementModal = useEditEngagementModal();
  const addRequirementToEngagementModal = useAddRequirementToEngagementModal();
  const addEngagementModal = useCreatePayeeEngagementModal();
  const fetchDefaultActions = useEngagementTypeDefaultActions();
  const featureFlags = useFeatureFlags();

  const workDefinitions = queryWorkDefinitions.data || [];
  const hasWorkDefinitions = workDefinitions.length > 0;

  return (
    <WSPage>
      <WSQueries queries={{ queryEngagement }}>
        {({ queryEngagementData: engagement }) => {
          const requirementsSection = (
            <>
              <WSSectionToolbar
                mb="M"
                title="Requirements"
                pill={{
                  theme: "neutral",
                  outline: true,
                  text: `${engagement.requirementDefinitionIds?.length ?? 0}`
                }}
                button={{
                  label: "Add requirement",
                  onClick: () => {
                    addRequirementToEngagementModal.open({
                      requirementDefinitionIds:
                        engagement.requirementDefinitionIds,
                      engagementId: engagement.engagementId
                    });
                  }
                }}
              />
              <RequirementsTable engagement={engagement} />
            </>
          );

          return (
            <>
              <PageHeader
                title="Engagements"
                rootPath={getSectionPathByEngagementStatus(engagement.status)}
                breadcrumbs={[
                  {
                    label: "Engagements",
                    onClick: () => {
                      goToEngagementTypes(engagement.status);
                    }
                  },
                  {
                    label: engagement.name
                  }
                ]}
                fetchMenuItems={() =>
                  fetchDefaultActions({
                    engagement,
                    onDelete: engagement => {
                      goToEngagementTypes(engagement.status);
                    }
                  })
                }
                menuItems={[
                  {
                    icon: "edit-box",
                    label: "Edit information",
                    onClick: () => {
                      editEngagementModal.open({ engagement });
                    }
                  },
                  {
                    icon: "clipboard-check",
                    label: "Add requirement",
                    onClick: () => {
                      addRequirementToEngagementModal.open({
                        requirementDefinitionIds:
                          engagement.requirementDefinitionIds,
                        engagementId: engagement.engagementId
                      });
                    }
                  },
                  {
                    icon: "user",
                    label: "Add assignment",
                    onClick: () => {
                      addEngagementModal.open(
                        {
                          engagementId: engagement.engagementId
                        },
                        { title: "Add assignment" }
                      );
                    }
                  }
                ]}
              />
              <WSFlexBox.CenterY mt="L" mb="2XL" gap="M">
                <WSText.Heading3 singleLine>{engagement.name}</WSText.Heading3>
                {engagement.status === EngagementStatus.Inactive ? (
                  <WSPill theme="neutral" text="Archived" icon={false} />
                ) : engagement.status === EngagementStatus.Active ? (
                  <WSPill theme="success" text="Active" icon={true} />
                ) : null}
                <WSText.ParagraphXs color="gray500">
                  Last modified {toWSDate(engagement.updatedAt).formattedValue}
                </WSText.ParagraphXs>
              </WSFlexBox.CenterY>

              <WSGrid gutter="2XL">
                <WSGrid.Item span={{ m: "8" }}>
                  {/* Engagement information */}
                  <WSSectionToolbar
                    mb="M"
                    title="Engagement information"
                    button={{
                      label: "Edit",
                      onClick: () => {
                        editEngagementModal.open({ engagement });
                      }
                    }}
                  />
                  <EngagementInfoPanel
                    name={engagement.name}
                    description={engagement.description}
                    mb="XL"
                  />

                  {/* Work definitions and Requirements */}
                  {featureFlags.data?.displayWorkDefinitions &&
                  hasWorkDefinitions ? (
                    <WSTabs
                      tabs={[
                        {
                          label: "Work definitions",
                          render: () => (
                            <TableWorkDefinitions
                              workDefinitions={workDefinitions}
                            />
                          )
                        },
                        {
                          label: "Requirements",
                          render: () => requirementsSection
                        }
                      ]}
                    />
                  ) : (
                    requirementsSection
                  )}
                </WSGrid.Item>

                <WSGrid.Item span={{ m: "4" }}>
                  <WSList gap="L">
                    {featureFlags.data?.rateCardOverride &&
                      engagement.rateCardId && (
                        <RateCardViewWidget
                          rateCardId={engagement.rateCardId}
                          context="engagement"
                          canCustomize={true}
                        />
                      )}
                    <RecentContractorsWidget
                      engagementId={engagement.engagementId}
                    />
                    <EngagementTypeHistoryWidget
                      engagementTypeId={engagementTypeId}
                    />
                    {/*
                  <WSDivider my="XL" />
                  <WSSectionToolbar title="Recent payables" />
                  <RecentPayablesTable payables={[]} /> */}
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
